import { supabase } from '../supabase/client'

/**
 * Storage objects are not reached by the database's own cascades. Deleting a
 * job removes its photo, node and report *rows*, but every photo, thumbnail
 * and report PDF stays in its bucket forever — 63 such orphans (28 MB) had
 * accumulated by the 2026-09-25 audit.
 *
 * Two constraints shape every function here:
 *
 * 1. **Delete the files before the rows.** Every bucket policy gates on
 *    `has_job_access(foldername(name)[1])`, which reads the `jobs` row. Once
 *    the job is gone the check fails and the objects can never be removed.
 * 2. **`storage.remove()` does not fail loudly.** An RLS refusal comes back
 *    as a 200 with an empty `data` array, not an error, so a silent policy
 *    gap looks exactly like success. `removeObjects` compares the returned
 *    count against what it asked for and reports the shortfall.
 *
 * Removal is best-effort by design: it must never block the delete the user
 * asked for. Whatever it leaves behind is an orphan, which the reconciliation
 * sweep (`misc/scripts/storage-sweep.ts`) collects later.
 */

/** Buckets keyed by job id in the first path segment. */
const JOB_SCOPED_BUCKETS = ['photos', 'reports', 'job-files'] as const

/** storage.list() pages; ask for the documented maximum. */
const LIST_PAGE_SIZE = 100

export interface CleanupResult {
  removed: number
  /** Paths the API accepted but did not report back — usually an RLS refusal */
  missed: string[]
}

const emptyResult = (): CleanupResult => ({ removed: 0, missed: [] })

/**
 * Remove objects from one bucket, in batches, tolerating partial success.
 * Returns what actually went away rather than trusting the call.
 */
export async function removeObjects(
  bucket: string,
  paths: (string | null | undefined)[]
): Promise<CleanupResult> {
  const unique = [...new Set(paths.filter((p): p is string => !!p))]
  if (unique.length === 0) return emptyResult()

  const result = emptyResult()
  for (let i = 0; i < unique.length; i += LIST_PAGE_SIZE) {
    const batch = unique.slice(i, i + LIST_PAGE_SIZE)
    try {
      const { data, error } = await supabase.storage.from(bucket).remove(batch)
      if (error) throw error
      const removed = new Set((data ?? []).map((o) => o.name))
      result.removed += removed.size
      result.missed.push(...batch.filter((p) => !removed.has(p)))
    } catch (error) {
      console.warn(`Storage cleanup failed for ${bucket}:`, error)
      result.missed.push(...batch)
    }
  }
  return result
}

/** Every object under a `{prefix}/` folder, following pagination. */
async function listFolder(bucket: string, prefix: string): Promise<string[]> {
  const paths: string[] = []
  for (let offset = 0; ; offset += LIST_PAGE_SIZE) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .list(prefix, { limit: LIST_PAGE_SIZE, offset })
    if (error) {
      console.warn(`Could not list ${bucket}/${prefix}:`, error)
      return paths
    }
    // A zero-byte placeholder row Supabase returns for empty folders has no id
    paths.push(...(data ?? []).filter((o) => o.id).map((o) => `${prefix}/${o.name}`))
    if (!data || data.length < LIST_PAGE_SIZE) return paths
  }
}

/**
 * Remove everything a job owns across all three job-keyed buckets.
 *
 * Listing the folder rather than reading `photos.storage_path` is deliberate:
 * it also collects thumbnails and any file whose row has already gone, so a
 * job delete leaves nothing behind even when earlier deletes failed.
 *
 * MUST be called while the job row still exists (see note 1 above).
 */
export async function purgeJobStorage(jobId: string): Promise<CleanupResult> {
  const result = emptyResult()
  for (const bucket of JOB_SCOPED_BUCKETS) {
    const paths = await listFolder(bucket, jobId)
    const one = await removeObjects(bucket, paths)
    result.removed += one.removed
    result.missed.push(...one.missed)
  }
  return result
}

/**
 * Remove the photos (and thumbnails) belonging to one inspection node.
 *
 * Photo keys are job-scoped, not node-scoped, so unlike a job purge this has
 * to read the rows — and therefore has to run before the node is deleted and
 * takes its photo rows with it.
 */
export async function purgeNodeStorage(nodeId: string): Promise<CleanupResult> {
  const { data, error } = await supabase
    .from('photos')
    .select('storage_path, thumbnail_path')
    .eq('node_id', nodeId)

  if (error) {
    console.warn('Could not list node photos for storage cleanup:', error)
    return emptyResult()
  }

  return removeObjects(
    'photos',
    (data ?? []).flatMap((p) => [p.storage_path, p.thumbnail_path])
  )
}

/**
 * Remove one photo's own objects. The thumbnail is a separate object and was
 * previously never deleted — 13 orphaned thumbnails at the time of the audit.
 */
export async function purgePhotoStorage(photo: {
  storage_path: string | null
  thumbnail_path: string | null
}): Promise<CleanupResult> {
  return removeObjects('photos', [photo.storage_path, photo.thumbnail_path])
}
